import { isNodeApproved } from '@/service/graph'
import { NODE_TYPE_COLORS } from '@/constants/graph'

// The three P&ID editor view layers (PRD "Vision HITL Labeling Implementation
// Spec", 2026-07-13):
//  - original: the raw drawing, always available as reference (overlay).
//  - detected: everything the pipeline found, in the normal per-class colors.
//  - review:   only the flagged (low-confidence) items in one hot color; every
//              other detection muted to a neutral grey so the rare cases stand out.
export type PnidLayer = 'original' | 'detected' | 'review'

// Confidence cutoff for the Review layer. PRD default: flag under 80%. The
// slider moves it between MIN (only the worst) and MAX (catch more).
export const REVIEW_CONFIDENCE_THRESHOLD = 0.8
export const REVIEW_THRESHOLD_MIN = 0.5
export const REVIEW_THRESHOLD_MAX = 0.9

/**
 * True when a detected node should be flagged for HITL review at the given
 * cutoff.
 *
 * - Hand-drawn (`source === 'manual'`) objects have no model score → never flagged.
 * - An already-approved node has been human-confirmed. Sachin's PRD note: "there
 *   are genuine cases where confidence is low but they're true positives", so
 *   approving removes an item from review rather than deleting it.
 * - The SigLIP `trust` score (VISN-291) is preferred over raw `confidence` when
 *   present: it is class-relative (0.84 can be normal for one class and alarming
 *   for another) and folds in competing-class margin + same-package anchor
 *   corroboration — exactly the "confidence is low but it's a true positive"
 *   cases the PRD note describes. Raw confidence remains the fallback for
 *   parses that predate trust.
 * - A node with neither score is left unflagged (nothing to judge).
 */
export function isFlaggedForReview(
  node: { confidence?: number | null; trust?: number | null; approved?: boolean; source?: string | null },
  cutoff: number = REVIEW_CONFIDENCE_THRESHOLD,
): boolean {
  if (node.source === 'manual') return false
  if (isNodeApproved(node)) return false
  const score = node.trust ?? node.confidence
  return score != null && score < cutoff
}

/**
 * Human-readable label for a flagged item in the review queue: the node's tag
 * text / symbol name if present, otherwise its type. Kept structural so it's
 * testable without constructing full node objects.
 */
export function reviewItemLabel(node: {
  type: string
  label?: string | null
  content?: string | null
  name?: string | null
}): string {
  for (const candidate of [node.label, node.content, node.name]) {
    if (candidate && candidate.trim()) return candidate.trim()
  }
  return node.type
}

export interface ReviewQueueItem {
  id: string
  label: string
  confidence?: number
  // SigLIP trust score (VISN-291) — the value the flagging actually used when
  // present; shown as the item's % with raw confidence kept for tooltips.
  trust?: number
}

type ReviewQueueNode = {
  id: string
  type: string
  x?: number
  y?: number
  confidence?: number | null
  trust?: number | null
  approved?: boolean
  source?: string | null
  label?: string | null
  content?: string | null
  name?: string | null
}

/**
 * Build the per-sheet review queue: the flagged symbol/tag/connection nodes at
 * the given threshold, in reading order (top→bottom, then left→right), each with
 * a display label + confidence. Pure so the filter/sort/label pipeline is
 * testable without rendering the editor.
 */
export function buildReviewQueue(
  nodes: ReviewQueueNode[],
  threshold: number,
  // VISN-291: text is deterministic on this pipeline (PyMuPDF embedded layer,
  // ~97% of tags at confidence 1.0), so symbol classification review is the
  // default; flip to false for the text-review workflow (mixed queue).
  symbolsOnly: boolean = true,
): ReviewQueueItem[] {
  return nodes
    .filter(
      (n) =>
        (symbolsOnly
          ? n.type === 'symbol'
          : n.type === 'symbol' || n.type === 'tag' || n.type === 'connection') &&
        isFlaggedForReview(n, threshold),
    )
    .sort((a, b) => (a.y ?? 0) - (b.y ?? 0) || (a.x ?? 0) - (b.x ?? 0))
    .map((n) => ({
      id: n.id,
      label: reviewItemLabel(n),
      confidence: n.confidence ?? undefined,
      trust: n.trust ?? undefined,
    }))
}

/**
 * Review-layer styling override for a single node's bounding box.
 *  - flagged → the one hot review color, full opacity.
 *  - not flagged & muted (default) → neutral grey at low opacity.
 *  - not flagged & `fullColor` → `{}` (keep the base per-class color / full opacity).
 *
 * An empty/partial result means "leave the base value untouched", so the caller
 * only overrides what's returned.
 */
export function reviewStyleFor(
  node: { type?: string; confidence?: number | null; trust?: number | null; approved?: boolean; source?: string | null },
  threshold: number,
  fullColor: boolean,
  symbolsOnly: boolean = true,
): { color?: string; opacity?: number } {
  const eligible = !symbolsOnly || node.type === 'symbol'
  if (eligible && isFlaggedForReview(node, threshold)) return { color: NODE_TYPE_COLORS.review, opacity: 1 }
  if (!fullColor) return { color: NODE_TYPE_COLORS.reviewMuted, opacity: 0.15 }
  return {}
}
